/**
 * Normalized Maintenance Jobs Hook
 * Single source of truth for maintenance jobs with calculated compliance status.
 *
 * Composes existing domain hooks and applies the same normalization logic
 * used by PlannedMaintenanceTab, ensuring Dashboard and Module show identical
 * compliance percentages.
 *
 * Returns jobs enriched with: lastCompleted, nextDue, daysUntilDue, status
 * Also returns pre-calculated stats from calculateMaintenanceStats()
 */

import { useMemo } from 'react';
import { useMaintenanceJobsDomain } from './useMaintenanceJobsDomain';
import { useMaintenanceHistory } from './useMaintenanceHistory';
import { useMaintenanceConfig } from './useMaintenanceConfig';
import { useEquipmentRegister } from '../useEquipmentRegister';
import { summarizeJob } from '../../../services/equipmentMaintenanceService';
import { getJobFrequencyDays } from '../../../utils/intervalHelpers';
import { calculateMaintenanceStats } from '../../../components/tailwind/modules/maintenance/utils/maintenanceHelpers';

/**
 * Parse date string as local time to avoid timezone shift.
 * new Date('2026-03-25') parses as UTC midnight, which can shift by ±1 day locally.
 */
function parseLocalDate(dateStr) {
  if (!dateStr) return null;
  if (typeof dateStr === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(dateStr)) {
    const [year, month, day] = dateStr.split('-').map(Number);
    return new Date(year, month - 1, day);
  }
  return new Date(dateStr);
}

/**
 * Hook that returns normalized maintenance jobs with calculated status.
 * Used by both Dashboard and PlannedMaintenanceTab for consistent compliance scoring.
 */
export function useNormalizedMaintenanceJobs() {
  // Compose existing domain hooks
  const { list: rawJobs, loaded: jobsLoaded } = useMaintenanceJobsDomain();
  const { history: maintenanceHistory } = useMaintenanceHistory();
  const { config: maintenanceSettings } = useMaintenanceConfig();
  const { items: equipment } = useEquipmentRegister();

  // Normalize jobs with calculated status (same logic as PlannedMaintenanceTab)
  const normalizedJobs = useMemo(() => {
    if (!rawJobs || rawJobs.length === 0) {
      return [];
    }

    // Create equipment map for summarizeJob
    const equipmentMap = new Map((equipment || []).map(eq => [eq.id, eq]));

    // Get advanceWarningDays from settings (default 30)
    const advanceWarningDays = maintenanceSettings?.advanceWarningDays || 30;

    return rawJobs.map(job => {
      // Get lastCompleted and nextDue from maintenance history
      const summary = summarizeJob(job, equipmentMap, maintenanceHistory);
      const lastCompleted = summary.lastCompleted;

      // Calculate nextDue and daysUntilDue
      let nextDue = null;
      let daysUntilDue = null;

      const today = new Date();
      today.setHours(0, 0, 0, 0);

      if (summary.nextDue) {
        // Has explicit nextDue from service history
        nextDue = parseLocalDate(summary.nextDue);
        nextDue.setHours(0, 0, 0, 0);
        daysUntilDue = Math.ceil((nextDue - today) / (1000 * 60 * 60 * 24));
      } else if (lastCompleted) {
        // Calculate nextDue from lastCompleted + interval
        const lastDate = parseLocalDate(lastCompleted);
        const intervalDays = getJobFrequencyDays(job);
        nextDue = new Date(lastDate);
        nextDue.setDate(nextDue.getDate() + intervalDays);
        nextDue.setHours(0, 0, 0, 0);
        daysUntilDue = Math.ceil((nextDue - today) / (1000 * 60 * 60 * 24));
      } else if (job.initialNextDue) {
        // Has initial due date set at job creation
        nextDue = parseLocalDate(job.initialNextDue);
        nextDue.setHours(0, 0, 0, 0);
        daysUntilDue = Math.ceil((nextDue - today) / (1000 * 60 * 60 * 24));
      } else {
        // Never completed AND no initial due date - due today
        nextDue = today;
        daysUntilDue = 0;
      }

      // Unified status logic - ALL jobs use daysUntilDue
      let status;
      if (daysUntilDue < 0) {
        status = 'overdue';     // Past due → red
      } else if (daysUntilDue <= advanceWarningDays) {
        status = 'due_soon';    // Within threshold → yellow
      } else {
        status = 'compliant';   // All good → green
      }

      return {
        ...job,
        lastCompleted,
        nextDue,
        daysUntilDue,
        status
      };
    });
  }, [rawJobs, equipment, maintenanceSettings?.advanceWarningDays, maintenanceHistory]);

  // Calculate stats from normalized jobs
  const stats = useMemo(() => {
    return calculateMaintenanceStats(normalizedJobs);
  }, [normalizedJobs]);

  return {
    normalizedJobs,
    stats,
    loaded: jobsLoaded
  };
}
